import { PayloadAction } from '@reduxjs/toolkit';
import {
  gridPanelAvailableTabChanged,
  gridPanelOpened,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import {
  contentWidthChanged,
  selectPinnedContentWidth,
} from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import {
  screenshareAdded,
  selectScreenshareStreams,
} from 'features/layout/features/order/orderSlice';
import { ScreenshareSourceDetails } from 'features/layout/types';
import { screenshareSelected } from 'features/screenshare/actions';
import { ScreenshareStreamingStarted } from 'features/streaming/types';
import { SelectedScreenshare } from 'features/screenshare/types';
import {
  selectSelectedScreenshare,
  selectShowAllScreens,
} from 'features/screenshare/screenshareSlice';
import { subscribeToScreenshareSaga } from 'features/screenshare/sagas/subscribeToScreenshareSaga';
import { formatScreenshareBoardState } from 'features/content-whiteboard/utils/formatScreenshareBoardState';
import { ContentData } from 'features/layout/features/content/types';
import { UserId } from 'features/users/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import { openModal } from 'features/modal/modalSlice';
import { call, put, select } from 'redux-saga/effects';
import { eventBus } from 'utils/eventBus';
import { RTCClient } from 'utils/webrtc';

export function* onScreensharingStartSaga(action: PayloadAction<ScreenshareStreamingStarted>) {
  const { id: userId, feedId, streams, whiteboard } = action.payload;
  const currentSelectedScreenshare: SelectedScreenshare | null =
    yield select(selectSelectedScreenshare);
  const localUserId: UserId = yield select(selectLocalUserId);

  if (RTCClient.isMobileScreenshareFeedId(feedId)) {
    return;
  }

  const isExistScreensharingStream = streams.some(
    (stream) => RTCClient.screensharingByUserId[userId]?.[stream.type]
  );
  const isLocalScreensharingReconnecting = Boolean(
    RTCClient.localFeeds[feedId] && RTCClient.screensharingFeed.isReconnecting
  );
  const isNotCurrentSelectedScreenshare = Boolean(
    currentSelectedScreenshare &&
    currentSelectedScreenshare.feedId !== feedId &&
    currentSelectedScreenshare.userId !== userId
  );

  yield call(RTCClient.receivingFeed.addAvailableStreams, feedId, streams);
  RTCClient.receivingFeed.availableScreenshares[feedId] = streams;

  const showAllScreens: boolean = yield select(selectShowAllScreens);

  const whiteboardShapes: string[] = yield call(formatScreenshareBoardState, whiteboard);

  const lockedContent: ContentData = yield select(selectLockedContent);

  const isReinit =
    (isExistScreensharingStream || isLocalScreensharingReconnecting) &&
    isNotCurrentSelectedScreenshare;

  const screenshares: ScreenshareSourceDetails[] = yield select(selectScreenshareStreams);
  const knownScreenshare = screenshares.find((stream) => stream.feedId === feedId);

  if (!showAllScreens) {
    yield put(
      screenshareSelected({
        userId,
        feedId,
        whiteboardShapes,
        reinit: isReinit,
        initial: true,
        existingScreenshare: !!knownScreenshare,
      })
    );
  }

  if (showAllScreens && localUserId !== userId) {
    yield call(subscribeToScreenshareSaga, userId, feedId, streams, { videoOnly: true });

    yield call(RTCClient.receivingFeed.updateScreenshareSimulcastConfig);
  }

  const hasVideoStream = streams?.some((item) => item.type === 'video');

  if (
    RTCClient.localFeeds[feedId] &&
    RTCClient.screensharingFeed.isReconnecting &&
    hasVideoStream
  ) {
    yield call(RTCClient.screensharingFeed.completeReconnect);
  }

  const source: ScreenshareSourceDetails = {
    userId: action.payload.id,
    feedId,
    kind: `screenshare-${RTCClient.localFeeds[feedId] ? 'local' : 'remote'}`,
    timestamp: knownScreenshare?.timestamp ?? new Date().getTime(),
  };

  yield put(screenshareAdded(source));

  if (lockedContent && lockedContent.id !== feedId && localUserId !== userId) {
    yield put(gridPanelAvailableTabChanged({ type: 'screenshare', active: true }));
    return;
  }

  if (!knownScreenshare) {
    yield put(gridPanelOpened('screenshare'));
  }

  if (
    lockedContent &&
    lockedContent.id !== feedId &&
    localUserId === userId &&
    !knownScreenshare &&
    !isLocalScreensharingReconnecting
  ) {
    yield put(openModal('pushLockedContentConfirm'));
  }

  const pinnedContentWidth: number = yield select(selectPinnedContentWidth);
  yield put(contentWidthChanged(pinnedContentWidth));
  yield call(eventBus.sendMessage, 'screenshareStarted', { userId });
}
